import { v4 as uuidv4 } from "uuid";

import {
  AthletePropertyValue,
  CoachedAthlete,
} from "@/app/coach/features/coach-profile/services/coach-services.ts";
import { kvBatch, kvGet, kvGetAllKeys } from "@/lib/storage/kv-client.ts";
import { BatchOperation } from "@/lib/storage/types.ts";
import { AthleteUserLinkService } from "./athlete-user-link-service.ts";

// Sport Profile interfaces
export interface PowerProfile {
  criticalPower: number; // watts
  wPrime: number; // joules (w')
}

export interface SpeedProfile {
  criticalSpeed: number; // m/s or km/h
  dPrime: number; // meters (d')
}

export interface HeartRateProfile {
  resting?: number; // bpm
  threshold?: number; // bpm
  maximum?: number; // bpm
}

export interface CyclingProfile {
  type: "cycling";
  power?: PowerProfile;
  heartRate?: HeartRateProfile;
}

export interface RunningProfile {
  type: "running";
  speed?: SpeedProfile;
  heartRate?: HeartRateProfile;
}

export type SportProfile = CyclingProfile | RunningProfile;

export interface AthleteProfile {
  uid: string; // Our permanent UUID
  role: "athlete";
  name: string;
  firstName: string;
  lastName: string;
  nickname: string;
  personalisedMessage: string;
  coaches: {
    coachId: string;
    primary: boolean;
    name: string;
    access?: "full" | "read-only"; // Coach's access level to this athlete
  }[];
  properties: AthletePropertyValue[];
  metrics: {
    height?: number;
    weight?: number;
  };
  settings: {
    theme?: "light" | "dark";
  };
  sportProfiles?: SportProfile[];
  createdAt: number;
  inviteCode?: string;
}

export interface AthleteUpdateParams {
  name?: string;
  firstName?: string;
  lastName?: string;
  nickname?: string;
  personalisedMessage?: string;
  metrics?: Partial<AthleteProfile["metrics"]>;
  settings?: Partial<AthleteProfile["settings"]>;
  properties?: AthletePropertyValue[];
  sportProfiles?: SportProfile[];
}

/**
 * Get the display name for an athlete.
 * Returns nickname if available, otherwise returns "firstName lastName".
 * If neither nickname nor first/last name are available, returns the default name field.
 */
export function getAthleteDisplayName(athlete: AthleteProfile): string {
  // Prefer nickname if it exists and is not empty
  if (athlete.nickname && athlete.nickname.trim() !== "") {
    return athlete.nickname.trim();
  }

  // Fall back to first + last name if both exist
  const firstName = athlete.firstName?.trim() || "";
  const lastName = athlete.lastName?.trim() || "";
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  // Final fallback to the name field
  return athlete.name || "Athlete";
}

export class AthleteManagementError extends Error {
  constructor(
    message: string,
    public code: "NOT_FOUND" | "UNAUTHORIZED" | "INVALID_OPERATION",
  ) {
    super(message);
    this.name = "AthleteManagementError";
  }
}

export class AthleteServices {
  static async createAthleteProfile(
    name: string,
    coachId: string,
    previousUid?: string, // used for migration
  ): Promise<AthleteProfile> {
    const uid = previousUid ?? uuidv4(); // This will be our permanent ID

    const athlete: AthleteProfile = {
      uid,
      role: "athlete",
      name,
      firstName: "",
      lastName: "",
      nickname: name,
      personalisedMessage: "",
      coaches: [],
      properties: [],
      metrics: {},
      settings: {
        theme: "dark",
      },
      createdAt: Date.now(),
    };

    try {
      const batchOps: BatchOperation[] = [
        {
          type: "set",
          key: `athlete:${uid}`,
          value: athlete,
          syncMetadata: {
            type: "ATHLETE_PROFILE",
            entityId: "Create athlete profile",
            userId: uid,
            createdBy: coachId,
          },
        },
      ];

      await kvBatch(batchOps);
      return athlete;
    } catch (error) {
      console.error("Failed to create athlete profile:", error);
      throw new AthleteManagementError(
        "Failed to create athlete profile",
        "INVALID_OPERATION",
      );
    }
  }

  static async getAthleteProfile(uid: string): Promise<AthleteProfile> {
    try {
      const athlete = await kvGet<AthleteProfile>(`athlete:${uid}`);

      if (!athlete) {
        throw new AthleteManagementError(
          "Athlete profile not found",
          "NOT_FOUND",
        );
      }

      return athlete;
    } catch (error) {
      if (error instanceof AthleteManagementError) {
        throw error;
      }
      console.error("Failed to get athlete profile:", error);
      throw new AthleteManagementError(
        "Failed to load athlete profile",
        "NOT_FOUND",
      );
    }
  }

  /**
   * Get all athletes assigned to a specific coach by querying athlete profiles.
   * This replaces storing the athletes array in the coach profile.
   */
  static async getAthletesForCoach(coachId: string): Promise<CoachedAthlete[]> {
    try {
      // Get all athlete keys
      const athleteKeys = await kvGetAllKeys("athlete:*");

      // Load all athlete profiles with background refresh
      const athletes = await Promise.all(
        athleteKeys.map(async (key) => {
          try {
            return await kvGet<AthleteProfile>(key, {
              backgroundRefresh: true, // Load from cache immediately, refresh in background
            });
          } catch (error) {
            console.warn(`Failed to load athlete ${key}:`, error);
            return null;
          }
        }),
      );

      // Filter athletes that have this coach
      const filteredAthletes = athletes.filter(
        (athlete): athlete is AthleteProfile => {
          if (!athlete) return false;
          return athlete.coaches?.some((coach) => coach.coachId === coachId);
        },
      );

      // Map to CoachedAthlete format with isSignedUp check
      const coachedAthletes = await Promise.all(
        filteredAthletes.map(async (athlete) => {
          const isLinked = await AthleteUserLinkService.isAthleteLinked(
            athlete.uid,
          );
          const coachInfo = athlete.coaches.find((c) => c.coachId === coachId);
          return {
            athleteId: athlete.uid,
            access: coachInfo?.access || "full", // Get access from athlete's coaches array
            name: athlete.name,
            firstName: athlete.firstName,
            lastName: athlete.lastName,
            nickname: athlete.nickname,
            isSignedUp: isLinked,
          };
        }),
      );

      return coachedAthletes;
    } catch (error) {
      console.error("Failed to get athletes for coach:", error);
      // Return empty array instead of throwing - allows graceful degradation
      return [];
    }
  }

  static async updateAthleteProfile(
    athleteId: string,
    params: AthleteUpdateParams,
    userId: string,
  ): Promise<AthleteProfile> {
    const currentProfile = await this.getAthleteProfile(athleteId);

    const updatedProfile: AthleteProfile = {
      ...currentProfile,
      ...params,
      metrics: {
        ...currentProfile.metrics,
        ...(params.metrics ?? {}),
      },
      settings: {
        ...currentProfile.settings,
        ...(params.settings ?? {}),
      },
    };

    // Auto-sync the 'name' field when firstName/lastName/nickname changes
    // This ensures backward compatibility with components still reading from 'name'
    if (params.firstName !== undefined || params.lastName !== undefined || params.nickname !== undefined) {
      updatedProfile.name = getAthleteDisplayName(updatedProfile);
    }

    try {
      await kvBatch([
        {
          type: "set",
          key: `athlete:${athleteId}`,
          value: updatedProfile,
          syncMetadata: {
            type: "ATHLETE_PROFILE",
            entityId: "Updated Athlete Profile",
            createdBy: userId,
            userId: athleteId,
          },
        },
      ]);
      console.log("update athlete profile ");
      return updatedProfile;
    } catch (error) {
      console.error("Failed to update athlete profile:", error);
      throw new AthleteManagementError(
        "Failed to update athlete profile",
        "INVALID_OPERATION",
      );
    }
  }

  static async updateAthleteCoaches(
    athleteId: string,
    coach: { coachId: string; name: string; primary: boolean },
    userId: string,
  ): Promise<AthleteProfile> {
    const currentProfile = await this.getAthleteProfile(athleteId);

    // Check if coach already exists in the list
    const coachExists = currentProfile.coaches.some(
      (c) => c.coachId === coach.coachId,
    );
    if (coachExists) {
      throw new AthleteManagementError(
        "Coach is already assigned to this athlete",
        "INVALID_OPERATION",
      );
    }

    const updatedProfile: AthleteProfile = {
      ...currentProfile,
      coaches: [...currentProfile.coaches, coach],
    };

    try {
      await kvBatch([
        {
          type: "set",
          key: `athlete:${athleteId}`,
          value: updatedProfile,
          syncMetadata: {
            type: "ATHLETE_PROFILE",
            entityId: "Update coaches athletes link",
            userId: athleteId,
            createdBy: userId,
          },
        },
      ]);

      // Emit sync event to notify listeners (e.g., coach profiles) of the update
      const { syncEventEmitter } = await import("@/lib/storage/sync-event-emitter.ts");
      syncEventEmitter.emit(`athlete:${athleteId}`, true);

      return updatedProfile;
    } catch (error) {
      console.error("Failed to update athlete coaches:", error);
      throw new AthleteManagementError(
        "Failed to update athlete coaches",
        "INVALID_OPERATION",
      );
    }
  }

  static async deleteAthleteProfile(
    athleteId: string,
    userId: string,
  ): Promise<string[]> {
    let athlete: AthleteProfile | null = null;
    let coachIds: string[] = [];
    let inviteCode: string | undefined;

    // Try to get the athlete profile, but don't fail if it doesn't exist
    // This handles corrupted/partial data gracefully
    try {
      athlete = await this.getAthleteProfile(athleteId);
      if (athlete) {
        coachIds = athlete.coaches.map((c) => c.coachId);
        inviteCode = athlete.inviteCode;
      }
    } catch (err) {
      // Profile not found or corrupted - log warning but continue with deletion
      console.warn(
        `[deleteAthleteProfile] Athlete profile ${athleteId} not found or corrupted, will delete related keys anyway`,
        err,
      );
    }

    try {
      // Always attempt to delete the main athlete key
      const batchOps: BatchOperation[] = [
        {
          type: "del",
          key: `athlete:${athleteId}`,
          syncMetadata: {
            type: "ATHLETE_PROFILE",
            entityId: "Delete athlete profile",
            userId: athleteId,
            createdBy: userId,
          },
        },
      ];

      // Always try to delete athlete-user link (may not exist)
      try {
        await AthleteUserLinkService.deleteLink(athleteId);
      } catch (err) {
        console.warn(
          `[deleteAthleteProfile] Failed to delete athlete-user link for ${athleteId}`,
          err,
        );
      }

      // If we found an invite code, delete that too
      if (inviteCode) {
        batchOps.push({
          type: "del",
          key: `athlete-invite:${inviteCode}`,
        });
      }

      // Also delete related keys (templates, views) if they exist
      // These are legacy/corrupted keys that shouldn't exist but need cleanup
      batchOps.push({
        type: "del",
        key: `athlete:${athleteId}:templates`,
      });
      batchOps.push({
        type: "del",
        key: `athlete:${athleteId}:views`,
      });

      await kvBatch(batchOps);

      // Emit sync event to notify listeners (e.g., coach profiles) of deletion
      const { syncEventEmitter } = await import("@/lib/storage/sync-event-emitter.ts");
      syncEventEmitter.emit(`athlete:${athleteId}`, true);

      // Return the list of coach IDs that had this athlete (empty if profile was missing)
      return coachIds;
    } catch (error) {
      console.error("[deleteAthleteProfile] Failed to delete athlete profile:", error);
      throw new AthleteManagementError(
        "Failed to delete athlete profile",
        "INVALID_OPERATION",
      );
    }
  }

  static async activateAthlete(
    inviteCode: string,
    firebaseUid: string,
    email: string,
  ): Promise<AthleteProfile> {
    // Get invitation details from unified invite registry
    const invitation = await kvGet<
      import("@/features/Auth/hooks/useInviteManagement").Invitation
    >(`invite:${inviteCode}`);
    if (!invitation) throw new Error("Invalid invite code");
    if (invitation.type !== "athlete" || !invitation.athleteId)
      throw new Error("Invite not linked to an athlete");
    const athleteId = invitation.athleteId;

    const athlete = await kvGet<AthleteProfile>(`athlete:${athleteId}`);
    if (!athlete) throw new Error("Athlete not found");

    // Create the athlete-user link using the new service
    await AthleteUserLinkService.createLink(athleteId, firebaseUid, email);

    // Clean up the invitation
    const currentCodes = (await kvGet<string[]>("invitations:codes")) ?? [];
    const updatedCodes = currentCodes.filter((c) => c !== inviteCode);

    await kvBatch([
      { type: "del", key: `invite:${inviteCode}` },
      { type: "set", key: "invitations:codes", value: updatedCodes },
    ]);

    return athlete;
  }

  static async getAthleteByFirebaseUid(
    firebaseUid: string,
  ): Promise<AthleteProfile | null> {
    const athleteId =
      await AthleteUserLinkService.getAthleteIdByFirebaseUid(firebaseUid);
    if (!athleteId) return null;

    return await kvGet<AthleteProfile>(`athlete:${athleteId}`);
  }

  /**
   * Check if an athlete is linked to a user account
   */
  static async isAthleteLinked(athleteId: string): Promise<boolean> {
    return await AthleteUserLinkService.isAthleteLinked(athleteId);
  }

  /**
   * Get the user link for an athlete (includes firebaseUid and email)
   */
  static async getAthleteUserLink(athleteId: string) {
    return await AthleteUserLinkService.getLinkByAthleteId(athleteId);
  }
}
